import { toast } from '@heroui/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import {
  Button,
  Description,
  Input,
  Label,
  ListBox,
  Modal,
  Select,
  Switch,
  TextField,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const spaceSchema = z.object({
  name: z.string().min(1, '空间名称不能为空').max(100, '空间名称长度不能超过100字符'),
  code: z
    .string()
    .max(50)
    .optional()
    .nullable()
    .refine(
      (val) => val == null || val === '' || /^[a-zA-Z0-9_-]+$/.test(val),
      '只能包含字母、数字、下划线和连字符'
    ),
  type: z.string().min(1, '请选择空间类型'),
  isPlatform: z.boolean().optional(),
  enterpriseType: z.string().optional().nullable(),
  industry: z.string().optional().nullable(),
  ownerId: z.string().min(1, '请选择所有者'),
  status: z.string().optional().nullable(),
  isVerified: z.boolean().optional(),
  logoUrl: z.string().url().max(255).optional().nullable(),
});

type SpaceFormValues = z.infer<typeof spaceSchema>;

type MetaField = {
  fieldName?: string;
  options?: Array<{ value: string; label: string }>;
};

const EMPTY_OPTIONS: Array<{ value: string; label: string }> = [];

interface EditorSpaceModalProps {
  open: boolean;
  space?: any;
  onCancel: () => void;
  onSuccess: () => void;
}

export function EditorSpaceModal({ open, space, onCancel, onSuccess }: EditorSpaceModalProps) {
  const utils = trpc.useUtils();
  const isEdit = !!space;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const { data: usersData } = trpc.users.list.useQuery(
    { page: '1', pageSize: '100' },
    { enabled: open && !isEdit }
  );
  const { data: formMeta } = trpc.meta.getMeta.useQuery(
    { source: 'spaces', scene: 'form' },
    { enabled: open }
  );

  const form = useForm<SpaceFormValues>({
    resolver: zodResolver(spaceSchema),
    defaultValues: {
      name: '',
      code: null,
      type: 'PERSONAL',
      isPlatform: false,
      enterpriseType: null,
      industry: null,
      ownerId: '',
      status: 'ACTIVE',
      isVerified: false,
      logoUrl: null,
    },
  });
  const { reset } = form;

  const fields = useMemo(
    () => ((formMeta as { fields?: MetaField[] })?.fields ?? []) as MetaField[],
    [formMeta]
  );
  const getFieldOptions = (fieldName: string) =>
    fields.find((field) => field.fieldName === fieldName)?.options ?? EMPTY_OPTIONS;
  const typeOptions = useMemo(() => getFieldOptions('type'), [fields]);
  const enterpriseTypeOptions = useMemo(() => getFieldOptions('enterpriseType'), [fields]);
  const industryOptions = useMemo(() => getFieldOptions('industry'), [fields]);
  const statusOptions = useMemo(() => getFieldOptions('status'), [fields]);

  const createSpaceMutation = trpc.spaces.create.useMutation({
    onSuccess: () => {
      toast.success('空间创建成功');
      form.reset();
      onSuccess();
      void utils.spaces.list.invalidate();
    },
    onError: (error) => {
      toast.danger('创建失败', { description: error.message });
    },
  });

  const updateSpaceMutation = trpc.spaces.update.useMutation({
    onSuccess: () => {
      toast.success('空间更新成功');
      form.reset();
      onSuccess();
      void utils.spaces.list.invalidate();
    },
    onError: (error) => {
      toast.danger('更新失败', { description: error.message });
    },
  });

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (isEdit && space) {
      reset({
        name: space.name || '',
        code: space.code || null,
        type: space.type || 'PERSONAL',
        isPlatform: space.isPlatform ?? false,
        enterpriseType: space.enterpriseType || null,
        industry: space.industry || null,
        ownerId: space.ownerId || '',
        status: space.status || 'ACTIVE',
        isVerified: space.isVerified || false,
        logoUrl: space.logoUrl || null,
      });
      return;
    }
    reset({
      name: '',
      code: null,
      type: typeOptions[0]?.value ?? 'PERSONAL',
      isPlatform: false,
      enterpriseType: null,
      industry: null,
      ownerId: '',
      status: statusOptions[0]?.value ?? 'ACTIVE',
      isVerified: false,
      logoUrl: null,
    });
  }, [open, isEdit, space, reset, typeOptions, statusOptions]);

  const onSubmit = async (values: SpaceFormValues) => {
    if (isEdit && space) {
      await updateSpaceMutation.mutateAsync({
        id: space.id,
        data: {
          name: values.name,
          code: values.code || null,
          type: values.type,
          isPlatform: values.isPlatform,
          enterpriseType: values.enterpriseType || null,
          industry: values.industry || null,
          ownerId: values.ownerId,
          status: values.status || undefined,
          isVerified: values.isVerified,
          logoUrl: values.logoUrl || null,
        },
      });
      return;
    }
    await createSpaceMutation.mutateAsync({
      name: values.name,
      code: values.code || null,
      type: values.type,
      isPlatform: values.isPlatform ?? false,
      enterpriseType: values.enterpriseType || null,
      industry: values.industry || null,
      ownerId: values.ownerId,
      status: values.status || 'ACTIVE',
      isVerified: values.isVerified || false,
      logoUrl: values.logoUrl || null,
    });
  };

  const isLoading = createSpaceMutation.isPending || updateSpaceMutation.isPending;
  const users = usersData?.items || [];
  const errors = form.formState.errors;

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog
            className="max-h-[90vh]"
            key={
              open
                ? isEdit && space
                  ? `edit-${String(space.id ?? '')}`
                  : 'create'
                : 'closed'
            }
          >
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">
                {isEdit ? '编辑空间' : '创建空间'}
              </Modal.Heading>
              <Description className="text-sm">
                {isEdit ? '修改空间信息' : '填写空间信息以创建新空间'}
              </Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-2">
              <form
                id="editor-space-form"
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex min-w-0 flex-col gap-4"
              >
                <div className="flex flex-col gap-2">
                  <Controller
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <TextField className="w-full" isInvalid={!!errors.name}>
                        <Label className="text-sm font-medium">
                          空间名称 <span className="text-danger">*</span>
                        </Label>
                        <Input
                          placeholder="请输入空间名称"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </TextField>
                    )}
                  />
                  {errors.name ? (
                    <p className="text-danger text-sm">{errors.name.message}</p>
                  ) : null}
                </div>

                {!isEdit ? (
                  <div className="flex flex-col gap-2">
                    <Controller
                      control={form.control}
                      name="code"
                      render={({ field }) => (
                        <TextField className="w-full" isInvalid={!!errors.code}>
                          <Label className="text-sm font-medium">空间代码</Label>
                          <Input
                            placeholder="请输入空间代码（可选）"
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(e.target.value === '' ? null : e.target.value)
                            }
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                          />
                        </TextField>
                      )}
                    />
                    <p className="text-muted-foreground text-xs">
                      空间代码用于系统内部标识，只能包含字母、数字、下划线和连字符
                    </p>
                    {errors.code ? (
                      <p className="text-danger text-sm">{errors.code.message as string}</p>
                    ) : null}
                  </div>
                ) : null}

                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">
                    空间类型 <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onChange={(key) => {
                          if (key != null) field.onChange(String(key));
                        }}
                      >
                        <Select.Trigger className="w-full">
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            {typeOptions.map((opt) => (
                              <ListBox.Item
                                key={opt.value}
                                id={opt.value}
                                textValue={opt.label}
                              >
                                {opt.label}
                                <ListBox.ItemIndicator />
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    )}
                  />
                  {errors.type ? (
                    <p className="text-danger text-sm">{errors.type.message}</p>
                  ) : null}
                </div>

                <div className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">是否平台空间</Label>
                    <p className="text-muted-foreground text-xs">平台空间用于系统级管理，通常仅一条</p>
                  </div>
                  <Controller
                    control={form.control}
                    name="isPlatform"
                    render={({ field }) => (
                      <Switch isSelected={!!field.value} onChange={field.onChange}>
                        <Switch.Content>
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Content>
                      </Switch>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">企业类型</Label>
                  <Controller
                    control={form.control}
                    name="enterpriseType"
                    render={({ field }) => (
                      <Select
                        value={field.value ?? '__none__'}
                        onChange={(key) => {
                          if (key == null) return;
                          const s = String(key);
                          field.onChange(s === '__none__' ? null : s);
                        }}
                      >
                        <Select.Trigger className="w-full">
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            <ListBox.Item id="__none__" textValue="不选择">
                              不选择
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                            {enterpriseTypeOptions.map((opt) => (
                              <ListBox.Item
                                key={opt.value}
                                id={opt.value}
                                textValue={opt.label}
                              >
                                {opt.label}
                                <ListBox.ItemIndicator />
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">所属行业</Label>
                  <Controller
                    control={form.control}
                    name="industry"
                    render={({ field }) => (
                      <Select
                        value={field.value ?? '__none__'}
                        onChange={(key) => {
                          if (key == null) return;
                          const s = String(key);
                          field.onChange(s === '__none__' ? null : s);
                        }}
                      >
                        <Select.Trigger className="w-full">
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            <ListBox.Item id="__none__" textValue="不选择">
                              不选择
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                            {industryOptions.map((opt) => (
                              <ListBox.Item
                                key={opt.value}
                                id={opt.value}
                                textValue={opt.label}
                              >
                                {opt.label}
                                <ListBox.ItemIndicator />
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    )}
                  />
                </div>

                {!isEdit ? (
                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-medium">
                      所有者 <span className="text-danger">*</span>
                    </Label>
                    <Controller
                      control={form.control}
                      name="ownerId"
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          onChange={(key) => {
                            if (key != null) field.onChange(String(key));
                          }}
                        >
                          <Select.Trigger className="w-full">
                            <Select.Value />
                            <Select.Indicator />
                          </Select.Trigger>
                          <Select.Popover>
                            <ListBox>
                              {users.map((user) => {
                                const id = user.id;
                                if (id == null) return null;
                                return (
                                  <ListBox.Item
                                    key={id}
                                    id={id}
                                    textValue={`${user.nickname || user.username} (${user.username})`}
                                  >
                                    {user.nickname || user.username} ({user.username})
                                    <ListBox.ItemIndicator />
                                  </ListBox.Item>
                                );
                              })}
                            </ListBox>
                          </Select.Popover>
                        </Select>
                      )}
                    />
                    <p className="text-muted-foreground text-xs">选择空间的所有者</p>
                    {errors.ownerId ? (
                      <p className="text-danger text-sm">{errors.ownerId.message}</p>
                    ) : null}
                  </div>
                ) : null}

                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">状态</Label>
                  <Controller
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <Select
                        value={field.value ?? '__none__'}
                        onChange={(key) => {
                          if (key == null) return;
                          const s = String(key);
                          field.onChange(s === '__none__' ? null : s);
                        }}
                      >
                        <Select.Trigger className="w-full">
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            <ListBox.Item id="__none__" textValue="不选择">
                              不选择
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                            {statusOptions.map((opt) => (
                              <ListBox.Item
                                key={opt.value}
                                id={opt.value}
                                textValue={opt.label}
                              >
                                {opt.label}
                                <ListBox.ItemIndicator />
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    )}
                  />
                  {errors.status ? (
                    <p className="text-danger text-sm">{errors.status.message}</p>
                  ) : null}
                </div>

                <div className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">是否认证</Label>
                    <p className="text-muted-foreground text-xs">空间是否已通过认证</p>
                  </div>
                  <Controller
                    control={form.control}
                    name="isVerified"
                    render={({ field }) => (
                      <Switch isSelected={!!field.value} onChange={field.onChange}>
                        <Switch.Content>
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Content>
                      </Switch>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Controller
                    control={form.control}
                    name="logoUrl"
                    render={({ field }) => (
                      <TextField className="w-full" isInvalid={!!errors.logoUrl}>
                        <Label className="text-sm font-medium">Logo URL</Label>
                        <Input
                          placeholder="请输入Logo URL（可选）"
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(e.target.value === '' ? null : e.target.value)
                          }
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </TextField>
                    )}
                  />
                  <p className="text-muted-foreground text-xs">空间的Logo图片URL</p>
                  {errors.logoUrl ? (
                    <p className="text-danger text-sm">{errors.logoUrl.message as string}</p>
                  ) : null}
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onCancel}>
                取消
              </Button>
              <Button
                isDisabled={isLoading}
                variant="primary"
                onPress={() => void form.handleSubmit(onSubmit)()}
              >
                {isLoading ? '提交中…' : isEdit ? '更新' : '创建'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
